//TODO: Refactor query keys (move some parameters to the main query keys)

import {
  AllocationEmailFilters,
  AllocationQueueFilters,
  AppType,
  FixDataListItem,
  MaintenanceType,
  TradeCommission,
  TradingFeeParams,
} from '@repo/types';

export const queryKeys = {
  auth: {
    login: {
      qr: ['auth', 'login', 'qr', 'start'],
    },
  },
  me: {
    all: ['me'],
  },
  securities: {
    all: ['securities'],
    autoCreateByDate: ['securities', 'auto_create_by_date'],
    details: (id: number) => ['securities', id],
    prefill: ['securities', 'prefill'],
    parameters: ['security_parameters'],
    hardCodePrice: (securityId: number) => [
      'securities',
      'security_hard_code_price',
      securityId,
    ],
  },
  confirmationSearch: {
    all: ['confirmation_search'],
    details: (id?: number) => ['confirmation_search', id],
  },
  comboBoxValue: {
    all: ['combo_box_value'],
  },
  settlementCycles: {
    all: ['settlement_cycles'],
    details: (id: number) => ['settlement_cycles', id],
  },
  salesTrader: {
    all: ['sales_trader'],
    statusOptions: ['sales_trader', 'status_options'],
  },
  salesTrading: {
    parameters: ['sales_trading_parameters'],
  },
  brokers: {
    all: ['brokers'],
  },
  commissionRebate: {
    all: ['commission_rebate'],
  },
  reportMarkupAccounts: {
    all: ['report_markup_accounts'],
  },
  tradeSearch: {
    all: ['trade_search'],
    versions: (internalTradeId: string, version: number) => [
      'trade_search',
      'versions',
      internalTradeId,
      version,
    ],
  },
  allocations: {
    emailDetails: (id: number) => ['allocations', 'emails', 'details', id],
    emails: (filters: AllocationEmailFilters) => [
      'allocations',
      'emails',
      filters,
    ],
    emailsAll: ['allocations', 'emails'],
    queue: (filters: AllocationQueueFilters) => [
      'allocations',
      'queue',
      filters,
    ],
    queueAll: ['allocations', 'queue'],
    queueDetails: (id: number) => ['allocations', 'queue', 'details', id],
    stats: ['allocations', 'stats'],
    tradeBlotterPreAllocations: (date: string, includeResolved = false) => [
      'allocations',
      'trade-blotter',
      'pre-allocations',
      date,
      includeResolved,
    ],
    tradeBlotterPreAllocationsAll: [
      'allocations',
      'trade-blotter',
      'pre-allocations',
    ],
  },
  goldmanMarqueeDeliveryConfig: {
    all: ['goldman_marquee_delivery_config'],
  },
  goldmanMarqueeSendDefaults: {
    byTradeDate: (tradeDate: string) => [
      'goldman_marquee_send_defaults',
      tradeDate,
    ],
  },
  goldmanMarqueeRunLogs: {
    list: (
      tradeDate?: string,
      triggerType?: string,
      status?: string,
      limit?: number,
    ) => [
      'goldman_marquee_run_logs',
      tradeDate ?? '',
      triggerType ?? '',
      status ?? '',
      limit ?? 50,
    ],
  },
  dailyConfirmations: {
    preview: (tradeDate: string) => [
      'daily_confirmations',
      'preview',
      tradeDate,
    ],
  },
  goldmanSachs: {
    all: ['goldman_sachs'],
  },
  clientRoot: {
    all: ['client_root'],
    broker: ['broker_root'],
    details: (id?: number) => ['client_root', id],
    funds: (id?: number) => ['client_root', 'client_root_funds', id],
    accounts: (clientRootId?: number) => ['client_root_accounts', clientRootId],
    accountDetails: (id?: number) => ['client_root_account_details', id],
    parameters: ['client_root_parameters'],
    parametersType: ['client_root_type_parameters'],
    moniker: ['client_root_moniker'],
    accountParameters: ['client_root_account_parameters'],
    existingAccounts: ['client_root_exiting_accounts'],
    accountName: (
      counterparty?: number,
      entityName?: string,
      clientMoniker?: string,
    ) => ['client_root_account_name', counterparty, entityName, clientMoniker],
  },
  parameters: {
    buySell: ['parameters', 'buy_sell'],
    ccy: ['parameters', 'ccy'],
    security: ['parameters', 'security'],
    salesTrader: ['parameters', 'sale_trader'],
    securityType: ['parameters', 'security_type'],
    securitySubtype: ['parameters', 'security_subtype'],
    account: ['parameters', 'account'],
    clientGroup: ['parameters', 'client_group'],
    clientRootGroups: (rootGroupId: number) => [
      'parameters',
      'client_root_groups',
      rootGroupId,
    ],
    clientGroupFund: ['parameters', 'client_group_fund'],
    side: ['parameters', 'side'],
    broker: ['parameters', 'broker'],
    feeType: ['parameters', 'fee_type'],
    counterparty: ['parameters', 'counterparty'],
    counterpartyFull: ['parameters', 'counterparty', 'full'],
    counterpartyData: ['parameters', 'counterparty', 'data'],
    market: ['parameters', 'market'],
    fileType: ['parameters', 'file_type'],
    clientContactType: ['parameters', 'client_contact_type'],
    contactTypes: ['parameters', 'contact_types'],
    internalFeeType: ['parameters', 'internal_fee_type'],
    valueType: ['parameters', 'value_type'],
    commissionType: ['parameters', 'commission_type'],
    futureContact: ['parameters', 'future_contact'],
    pset: ['parameters', 'pset'],
    exchangeType: ['parameters', 'exchange_type'],
    priceSource: ['parameters', 'price_source'],
    tradeType: ['parameters', 'trade_type'],
    tradeExecutedUsername: ['parameters', 'trade_executed_username'],
    allUsers: ['parameters', 'all_users'],
    clientType: ['parameters', 'client_type'],
  },
  dashboard: {
    analytics: ['dashboard', 'analytics'],
    lastMifidDate: ['last_mifid_date'],
    failedFixMessage: ['failed_fix_message'],
    lifecycleEvents: (asOfDate?: string) => ['lifecycle_events', asOfDate],
    missingBicsSectorProducts: ['missing_bics_sector_products'],
    preAllocationRecords: ['pre_allocation_records'],
    sftpStatus: (date?: string) => ['sftp_status', date],
  },
  version: {
    frontend: (appType?: AppType) => ['frontend_version', appType],
  },
  confirmation: {
    accountByGroup: (groupId: number) => [
      'confirmation_account_by_group',
      groupId,
    ],
    security: {
      all: ['confirmation_security'],
      details: (securityType: number) => [
        'confirmation_security',
        securityType,
      ],
    },
  },
  fixMessage: {
    bookedTrade: (executionId: number) => [
      'fix_message',
      'booked_trade',
      executionId,
    ],
    fixMessages: (items: string, date: string) => [
      'fix_messages',
      'fix_messages_item',
      items,
      date,
    ],
    allocationClose: (productId: number, productType: number) => [
      'fix_message',
      'allocation_close',
      productId,
      productType,
    ],
    all: ['fix_message', 'all'],
    confirmationEmail: (
      account: string,
      tradeDate: string,
      securityType: number,
      brokers: string[],
    ) => [
      'fix_message',
      'confirmation_email',
      account,
      tradeDate,
      securityType,
      ...(brokers ?? []),
    ],
    confirmationEmailFile: (
      tradeDate: string,
      fixMessages: FixDataListItem[],
    ) => [
      'fix_message',
      'confirmation_email_file',
      tradeDate,
      ...fixMessages.map((item) => item.id),
    ],
    allAggregation: ['fix_message', 'all_aggregation'],
  },
  corporateActions: {
    all: ['corporate_actions', 'all'],
    details: (id: number) => ['corporate_actions', 'details', id],
    ccy: (securityId: number) => ['corporate_actions', 'ccy', securityId],
    underlying: (securityId: number) => [
      'corporate_actions',
      'underlying',
      securityId,
    ],
  },
  parameterValueId: {
    all: ['parameter_value_id'],
  },
  parameter: {
    all: ['parameter'],
    details: (id: number) => ['parameter', id],
  },
  clientGroups: {
    all: ['client_groups'],
    details: (id: number) => ['client_groups', id],
  },
  markets: {
    all: ['markets'],
  },
  regions: {
    all: ['regions'],
  },
  client: {
    all: ['client'],
    allBroker: ['broker'],
    details: (id: number) => ['client', id],
    contacts: (id: number) => ['client', id, 'contacts'],
    groups: (id: number) => ['client', id, 'groups'],
    confirmationEmails: (id: number) => ['client', id, 'confirmation_emails'],
    internalFees: (id: number) => ['client', id, 'internal_fees'],
    internalFeeDetails: (id: number, feeId: number) => [
      'client',
      id,
      'internal_fees',
      feeId,
    ],
    tradingFees: (id: number) => ['client', id, 'trading_fees'],
    tradingFeeDetails: (id: number, feeId: number) => [
      'client',
      id,
      'trading_fees',
      feeId,
    ],
    commissions: (id: number) => ['client', id, 'commissions'],
    commissionDetails: (id: number, commissionId: number) => [
      'client',
      id,
      'commissions',
      commissionId,
    ],
    commissionVariables: (commissionId: number) => [
      'client',
      'commission_variables',
      commissionId,
    ],
    commissionVariablesDetails: (commissionId: number, variableId: number) => [
      'client',
      'commission_variables',
      commissionId,
      variableId,
    ],
    ssis: (id: number) => ['client', id, 'ssis'],
    ssisDetails: (id: number, ssisId: number) => ['client', id, 'ssis', ssisId],
  },
  openPositions: {
    all: ['open_positions'],
    summary: (groupId: string, reportDate: string) => [
      'open_positions',
      'summary',
      groupId,
      reportDate,
    ],
  },
  users: {
    list: (query: string) => ['users', 'list', query],
  },
  virtu: {
    file: (date: string, securityExchange: string) => [
      'virtu',
      'file',
      date,
      securityExchange,
    ],
  },
  exchange: {
    all: ['exchange'],
    details: (id: number) => ['exchange', 'details', id],
  },
  pricing: {
    all: ['pricing'],
    details: (id: number) => ['pricing', 'details', id],
    comboBox: (securityId: number) => ['pricing', 'combo_box', securityId],
  },
  settlementHoliday: {
    all: ['settlement_holiday'],
    details: (id: number) => ['settlement_holiday', 'details', id],
  },
  sharepointFolders: {
    all: ['sharepoint_folders'],
    path: (path?: string) => ['sharepoint_folders', path],
  },
  clientUnitSize: {
    all: ['client_unit_size'],
    details: (id: number) => ['client_unit_size', 'details', id],
  },
  custodyConfig: {
    all: ['custody_config'],
    configTableFields: (
      custodyConfigId: number,
      custodyFilePatternId?: number,
    ) => [
      'custody_config',
      'config_table_fields',
      custodyConfigId,
      custodyFilePatternId ?? null,
    ],
    details: (id: number) => ['custody_config', 'details', id],
    fileConfig: {
      all: (sftpConfigId: number) => [
        'custody_config',
        'file_config',
        'all',
        'sftp_config',
        sftpConfigId,
      ],
      details: (id: number) => ['custody_config', 'file_config', 'details', id],
    },
    sftpConfig: {
      all: (custodyConfigId: number) => [
        'custody_config',
        'sftp_config',
        'all',
        custodyConfigId,
      ],
      details: (id: number) => ['custody_config', 'sftp_config', 'details', id],
    },
    sftpFile: {
      all: (configId: number) => [
        'custody_config',
        'sftp_file',
        'all',
        configId,
      ],
      details: (id: number) => ['custody_config', 'sftp_file', 'details', id],
    },
  },
  custodyData: {
    cash: {
      all: (
        valueDate?: string,
        counterparty?: number | string,
        custodyConfig?: number | string,
        fromDate?: string,
        toDate?: string,
      ) => [
        'custody_data',
        'cash',
        valueDate ?? '',
        counterparty ?? '',
        custodyConfig ?? '',
        fromDate ?? '',
        toDate ?? '',
      ],
    },
    positions: {
      all: (
        valueDate?: string,
        counterparty?: number | string,
        fromDate?: string,
        toDate?: string,
      ) => [
        'custody_data',
        'positions',
        valueDate ?? '',
        counterparty ?? '',
        fromDate ?? '',
        toDate ?? '',
      ],
    },
    transactions: {
      all: (
        valueDate?: string,
        counterparty?: number | string,
        fromDate?: string,
        toDate?: string,
      ) => [
        'custody_data',
        'transactions',
        valueDate ?? '',
        counterparty ?? '',
        fromDate ?? '',
        toDate ?? '',
      ],
    },
  },
  reconciliation: {
    all: ['reconciliation'],
  },
  tradeEntry: {
    accountByGroup: (groupId: number) => [
      'trade_entry',
      'account_by_group',
      groupId,
    ],
    portfolioQuantity: (params: {
      clientAccount: number;
      productId: number;
      productType: number;
    }) => [
      'trade_entry',
      'portfolio_quantity',
      params.clientAccount,
      params.productId,
      params.productType,
    ],
    securityCodes: (securityId: number) => [
      'trade_entry',
      'security_codes',
      securityId,
    ],
    tradingFee: (params: TradingFeeParams) => [
      'trade_entry',
      'trading_fee',
      params.securityId,
      params.securityType,
      params.tradeDate,
      params.clientAccount,
      params.quantity,
      params.grossPrice,
      params.buySell,
      params.tradeDate,
    ],
    commission: (params: TradeCommission) => [
      'trade_entry',
      'commission',
      params.securityId,
      params.clientAccount,
      params.quantity,
      params.grossPrice,
    ],
    calculatedFees: (params: { client: number; tradeDate: string }) => [
      'trade_entry',
      'calculated_fees',
      params.client,
      params.tradeDate,
    ],
    checkQuantity: (params: {
      clientAccount: number;
      productId: number;
      productType: number;
    }) => [
      'trade_entry',
      'check_quantity',
      params.clientAccount,
      params.productId,
      params.productType,
    ],
    details: (id: number) => ['trade_entry', 'details', id],
    tradingFeeAmend: (tradeExecutedId: number) => [
      'trade_entry',
      'trading_fee_amend',
      tradeExecutedId,
    ],
  },

  tabs: {
    all: ['tabs'],
    details: (id: number) => ['tabs', 'details', id],
  },
  adminGroups: {
    all: ['admin_groups'],
    details: (id: number) => ['admin_groups', 'details', id],
  },
  adminUsers: {
    all: ['admin_users'],
    details: (id: number) => ['admin_users', 'details', id],
  },
  userPermissions: {
    all: ['user_permissions'],
    details: (id: number) => ['user_permissions', 'details', id],
    contentTypes: ['user_permissions', 'content_types'],
  },
  permissionTags: {
    all: ['permission_tags'],
  },
  adminProfiles: {
    all: ['admin_profiles'],
    details: (id: number) => ['admin_profiles', 'details', id],
    byUserId: (userId: number) => ['admin_profiles', 'by_user_id', userId],
  },
  adminClientGroups: {
    all: ['admin_client_groups'],
  },
  constants: {
    all: ['constants'],
  },
  maintenance: {
    all: ['maintenance'],
    details: (type: MaintenanceType) => ['maintenance', type],
  },
  sftp: {
    all: ['sftp'],
    details: (id: number) => ['sftp', 'details', id],
    filesAll: (configId: number) => ['sftp', 'files', 'all', configId],
    filesDetails: (id: number) => ['sftp', 'files', 'details', id],
  },
  externalContact: {
    all: ['external_contact'],
    details: (id: number) => ['external_contact', 'details', id],
  },
  tradersPl: {
    salesTrading: ['traders_pl', 'sales_trading'],
  },
  backup: {
    all: ['backup'],
  },
  timeline: {
    all: ['timeline'],
    details: (date: string) => ['timeline', date],
  },
  fixMessages: {
    all: ['fix_messages'],
    byDate: (date: string) => ['fix_messages', 'by_date', date],
  },
  patternRecognition: {
    tickers: (search?: string) => [
      'pattern_recognition',
      'tickers',
      search ?? '',
    ],
    rsiConfig: ['pattern_recognition', 'rsi_config'],
    rsiSignalsList: (date?: string) => [
      'pattern_recognition',
      'rsi_signals_list',
      date ?? '',
    ],
  },
  portfolios: {
    all: ['portfolios'],
    preview: (reportDate?: string) => ['portfolios', 'preview', reportDate],
    info: (groupId: number, reportDate?: string) => [
      'portfolios',
      'info',
      groupId,
      reportDate,
    ],
    details: (groupId: number, reportDate?: string) => [
      'portfolios',
      'details',
      groupId,
      reportDate,
    ],
    contributionTable: (
      groupId: number,
      fromDate?: string,
      toDate?: string,
      groupBy?: string,
    ) => [
      'portfolios',
      'contribution-table',
      groupId,
      fromDate,
      toDate,
      groupBy,
    ],
    positions: (
      groupId: number,
      asOfDate?: string,
      groupBy?: string,
      aggregate?: boolean,
    ) => ['portfolios', 'positions', groupId, asOfDate, groupBy, aggregate],
    transactions: (
      groupId: number,
      fromDate?: string,
      toDate?: string,
      dateType?: string,
    ) => ['portfolios', 'transactions', groupId, fromDate, toDate, dateType],
    groups: (clientRootId: number) => ['portfolios', 'groups', clientRootId],
    chart: (
      groupId: number,
      type?: string,
      fromDate?: string,
      toDate?: string,
    ) => ['portfolios', 'chart', groupId, type, fromDate, toDate],
    accounts: (groupId?: number) => ['portfolios', 'accounts', groupId],
    performance: (groupId: number, fromDate?: string, toDate?: string) => [
      'portfolios',
      'performance',
      groupId,
      fromDate,
      toDate,
    ],
  },
  notifications: {
    all: ['notifications'],
    details: (id: number) => ['notifications', 'details', id],
    image: (id: number) => ['notifications', 'image', id],
  },
  strategy: {
    all: ['strategy'],
    details: (id: number) => ['strategy', 'details', id],
  },
};
